import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, FileText, DollarSign, ExternalLink, Loader2, Check, Send, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtUSD2, fmtDate } from "@/lib/api";
import { quoteStatusLabel } from "@/lib/constants";
import { Pill } from "@/components/app/shared";
import QuoteBuilderDialog from "@/components/crm/QuoteBuilderDialog";
import InvoiceDialog from "@/components/crm/InvoiceDialog";
import SendQuoteDialog from "@/components/crm/SendQuoteDialog";
import { QuotePdfControls, QuoteEvidence } from "@/components/crm/QuoteDocs";

const QUOTE_TONE = { DRAFT: "neutral", SENT: "sky", VIEWED: "butter", ACCEPTED: "mint", DECLINED: "blush", EXPIRED: "neutral" };

export default function DealQuotesPanel({ deal }) {
  const qc = useQueryClient();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceQuote, setInvoiceQuote] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendQuote, setSendQuote] = useState(null);

  const { data: quotesData, isLoading: lq } = useQuery({
    queryKey: ["quotes", deal.id],
    queryFn: async () => (await api.get(`/quotes?dealId=${deal.id}`)).data,
    enabled: !!deal.id,
    refetchInterval: (query) => {
      const list = query?.state?.data?.quotes || [];
      const busy = list.some((x) => x.pdfStatus === "generating" || ["pending", "processing"].includes(x.acceptanceEvidence?.status));
      return busy ? 2500 : false;
    },
  });
  const [folderLink, setFolderLink] = useState(deal.driveFolderLink || "");
  const folder = useMutation({
    mutationFn: async () => (await api.post(`/deals/${deal.id}/drive-folder`)).data,
    onSuccess: (r) => { setFolderLink(r.link); window.open(r.link, "_blank", "noopener,noreferrer"); qc.invalidateQueries({ queryKey: ["deals"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const { data: invoicesData, isLoading: li } = useQuery({
    queryKey: ["invoices", deal.id],
    queryFn: async () => (await api.get(`/invoices?dealId=${deal.id}`)).data,
    enabled: !!deal.id,
  });
  const quotes = quotesData?.quotes || [];
  const invoices = invoicesData?.invoices || [];

  const markPaid = useMutation({
    mutationFn: async (iid) => (await api.post(`/invoices/${iid}/paid`, {})).data,
    onSuccess: (inv) => { toast.success(`Factura ${inv.invoiceNumber} marcada como pagada`); qc.invalidateQueries({ queryKey: ["invoices", deal.id] }); qc.invalidateQueries({ queryKey: ["deals"] }); qc.invalidateQueries({ queryKey: ["crm-revenue"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openNewQuote = () => { setQuoteId(null); setQuoteOpen(true); };
  const openQuote = (q) => { setQuoteId(q.id); setQuoteOpen(true); };
  const openNewInvoice = (fromQuote = null) => { setInvoiceQuote(fromQuote); setInvoiceOpen(true); };

  if (!deal?.id) return null;

  return (
    <section className="rounded-md border border-border bg-background p-4" data-testid="deal-quotes-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="kicker">Cotizaciones</div>
        <div className="flex items-center gap-1.5">
          {folderLink ? (
            <Button size="sm" variant="ghost" asChild data-testid="deal-drive-folder-link">
              <a href={folderLink} target="_blank" rel="noreferrer"><FolderOpen className="mr-1.5 h-3.5 w-3.5" /> Carpeta en Drive</a>
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => folder.mutate()} disabled={folder.isPending} data-testid="deal-drive-folder-create">
              {folder.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FolderOpen className="mr-1.5 h-3.5 w-3.5" />} Carpeta en Drive
            </Button>
          )}
          <Button size="sm" onClick={openNewQuote} data-testid="deal-new-quote-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Nueva cotización</Button>
        </div>
      </div>
      <div className="space-y-2">
        {lq ? <div className="h-10 animate-pulse rounded bg-muted" /> :
          quotes.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
              Aún no hay cotizaciones para este deal.
            </div>
          ) : quotes.map((q) => (
            <div key={q.id} className="rounded-md border border-border bg-card px-3 py-2.5" data-testid="deal-quote-item">
            <div className="flex items-center justify-between gap-3">
              <button onClick={() => openQuote(q)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid="deal-quote-open">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium">{q.quoteNumber}</div>
                  <div className="font-mono text-[10.5px] text-muted-foreground">{fmtDate(q.createdAt)} · {q.items?.length || 0} items</div>
                </div>
              </button>
              <div className="flex items-center gap-2">
                <Pill tone={QUOTE_TONE[q.status] || "neutral"} data-testid="deal-quote-status">{quoteStatusLabel(q.status)}</Pill>
                <span className="font-mono text-sm">{fmtUSD2(q.total)}</span>
                {(q.status === "DRAFT" || q.status === "SENT" || q.status === "VIEWED") && (
                  <Button size="sm" variant="outline" onClick={() => { setSendQuote(q); setSendOpen(true); }} data-testid="deal-quote-send-button">
                    <Send className="mr-1 h-3.5 w-3.5" /> {q.status === "DRAFT" ? "Enviar" : "Reenviar"}
                  </Button>
                )}
                {q.status === "ACCEPTED" && (
                  <Button size="sm" variant="outline" onClick={() => openNewInvoice(q)} data-testid="deal-quote-invoice-button">
                    <DollarSign className="mr-1 h-3.5 w-3.5" /> Facturar
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-2 border-t border-border pt-2">
              <QuotePdfControls q={q} dealId={deal.id} />
            </div>
            <QuoteEvidence q={q} dealId={deal.id} />
            </div>
          ))}
      </div>

      <div className="mt-5 mb-3 flex items-center justify-between">
        <div className="kicker">Facturas</div>
        <Button size="sm" variant="outline" onClick={() => openNewInvoice(null)} data-testid="deal-new-invoice-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Factura manual</Button>
      </div>
      <div className="space-y-2">
        {li ? <div className="h-10 animate-pulse rounded bg-muted" /> :
          invoices.length === 0 ? (
            <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
              Sin facturas emitidas aún.
            </div>
          ) : invoices.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-3 rounded-md border border-border bg-card px-3 py-2.5" data-testid="deal-invoice-item">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <DollarSign className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium">{inv.invoiceNumber} · {inv.documentType}</div>
                  <div className="font-mono text-[10.5px] text-muted-foreground">
                    Emitida {fmtDate(inv.issuedAt)}{inv.paidAt ? ` · Pagada ${fmtDate(inv.paidAt)}` : inv.dueAt ? ` · Vence ${fmtDate(inv.dueAt)}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Pill tone={inv.status === "PAID" ? "mint" : inv.status === "ISSUED" ? "butter" : "neutral"} data-testid="deal-invoice-status">
                  {inv.status === "PAID" ? "Pagada" : inv.status === "ISSUED" ? "Emitida" : inv.status === "VOID" ? "Anulada" : "Borrador"}
                </Pill>
                <span className="font-mono text-sm">{fmtUSD2(inv.amountTotal)}</span>
                {inv.pdfLink && <Button variant="ghost" size="icon" asChild className="h-7 w-7"><a href={inv.pdfLink} target="_blank" rel="noreferrer" aria-label="Abrir PDF"><ExternalLink className="h-3.5 w-3.5" /></a></Button>}
                {inv.status !== "PAID" && (
                  <Button size="sm" variant="outline" onClick={() => markPaid.mutate(inv.id)} disabled={markPaid.isPending} data-testid="deal-invoice-mark-paid">
                    {markPaid.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />} Marcar pagada
                  </Button>
                )}
              </div>
            </div>
          ))}
      </div>

      {quoteOpen && <QuoteBuilderDialog open={quoteOpen} onOpenChange={setQuoteOpen} deal={deal} quoteId={quoteId} />}
      {invoiceOpen && <InvoiceDialog open={invoiceOpen} onOpenChange={setInvoiceOpen} deal={deal} quote={invoiceQuote} />}
      {sendOpen && <SendQuoteDialog open={sendOpen} onOpenChange={setSendOpen} quote={sendQuote} />}
    </section>
  );
}
